import PropTypes from 'prop-types';
import { useContext, useEffect, useRef, useState } from 'react';
import { ThemeProvider } from 'emotion-theming';
import { useMediaQuery } from 'react-responsive';
import { MenuItem } from '@veeva/corgix/menu';
import Highlighter from 'react-highlight-words';
import OverlayContext from '../../containers/OverlayContext';
import {
    searchBoxMobileSearchBarCSS,
    searchBoxMobileSearchInputCSS,
    searchBoxMobileOverlayWidthCSS,
} from './styles/SearchBoxMobile.styles';
import {
    navigationBarSearchMobileCloseButtonCSS,
    searchInputMobileMenuCSS,
} from '../../../app-stats/components/styles/SearchInputMobile.styles';
import { searchBoxMenuItemCSS } from './styles/SearchBoxMenu.styles';
import { BREAKPOINTS } from '../../services/breakpoints';
import SearchBoxInput from './SearchBoxInput';
import SearchBoxMenu from './SearchBoxMenu';
import MenuDivider from '../MenuDivider';

import SearchBoxMenuItemType from './SearchBoxMenuItemType';
import { searchBoxMenuItemContentCSS } from './styles/SearchBoxMenuItem.styles';
import SearchBoxMenuItemIcon from './SearchBoxMenuItemIcon';

export default function SearchBoxMobile({
    className,
    placeholder,
    onItemSelected,
    onSearchTextChange,
    items,
    searchWords,
    selectedValue,
    searchText,
    showSearchResults,
    setShowSearchResults,
    setSearchParams,
}) {
    const inputRef = useRef(null);
    const inputContainerRef = useRef(null);
    const menuMapRef = useRef(null);
    const [focusedNodeKeyValue, setFocusedNodeKeyValue] = useState(undefined);
    const { setOverlay } = useContext(OverlayContext);
    const isAboveSizeThreshold = useMediaQuery({ minWidth: BREAKPOINTS.SMALL.min });
    useEffect(() => {
        if (isAboveSizeThreshold) {
            setOverlay(null);
        }
    }, [isAboveSizeThreshold, setOverlay]);

    return (
        <ThemeProvider
            theme={{
                iconSizeMD: '1.25rem',
            }}
        >
            <div css={searchBoxMobileSearchBarCSS}>
                <SearchBoxInput
                    css={searchBoxMobileSearchInputCSS}
                    className={className}
                    inputRef={inputRef}
                    inputContainerRef={inputContainerRef}
                    onSearchTextChange={onSearchTextChange}
                    placeholder={placeholder}
                    searchText={searchText}
                    showSearchResults={showSearchResults}
                    setShowSearchResults={setShowSearchResults}
                    focusedNodeKeyValue={focusedNodeKeyValue}
                    setFocusedNodeKeyValue={setFocusedNodeKeyValue}
                    selectedValue={selectedValue}
                    menuMapRef={menuMapRef}
                    onItemSelected={onItemSelected}
                    autoFocus
                    setSearchParams={setSearchParams}
                />
                <button
                    css={navigationBarSearchMobileCloseButtonCSS}
                    onClick={() => {
                        setOverlay(null);
                    }}
                    type="button"
                >
                    Close
                </button>
            </div>
            <SearchBoxMenu
                css={[searchInputMobileMenuCSS, searchBoxMobileOverlayWidthCSS]}
                inputContainerRef={inputContainerRef}
                focusedNodeKeyValue={focusedNodeKeyValue}
                setFocusedNodeKeyValue={setFocusedNodeKeyValue}
                onSearchItemSelected={onItemSelected}
                selectedValue={selectedValue}
                searchText={searchText}
                setShowSearchResults={setShowSearchResults}
                ref={menuMapRef}
                setSearchParams={setSearchParams}
            >
                {items.map((item) =>
                    item.type === SearchBoxMenuItemType.DIVIDER ? (
                        <MenuDivider key={`${item.key}`} />
                    ) : (
                        <MenuItem
                            css={searchBoxMenuItemCSS}
                            key={`${item.key}`}
                            keyValue={`${item.key}`}
                            value={`${item.key}`}
                        >
                            <div css={searchBoxMenuItemContentCSS}>
                                {SearchBoxMenuItemIcon(item.key)}
                                <Highlighter
                                    searchWords={searchWords ?? []}
                                    highlightTag="strong"
                                    autoEscape
                                    textToHighlight={item.label || ''}
                                />
                            </div>
                        </MenuItem>
                    ),
                )}
                {!items.length ? (
                    <MenuItem keyValue="not-found" value="not-found">
                        No results found
                    </MenuItem>
                ) : null}
            </SearchBoxMenu>
        </ThemeProvider>
    );
}

SearchBoxMobile.propTypes = {
    className: PropTypes.string,
    items: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string,
            label: PropTypes.string,
            type: PropTypes.oneOf(Object.values(SearchBoxMenuItemType)),
        }),
    ),
    onItemSelected: PropTypes.func,
    onSearchTextChange: PropTypes.func,
    placeholder: PropTypes.string,
    searchText: PropTypes.string,
    searchWords: PropTypes.arrayOf(PropTypes.string),
    selectedValue: PropTypes.string,
    setSearchParams: PropTypes.func,
    setShowSearchResults: PropTypes.func,
    showSearchResults: PropTypes.bool,
};
